import type { Track } from '@/world/Track';
import type { MoverSpec } from '@/config/cities';
import type { WallResolution } from '@/physics/collisions';

/**
 * movers.ts — PURE physics for the MOVING course obstacles (M99): city-themed
 * traffic that shuttles ACROSS the road on a fixed, fully deterministic
 * timetable (a yellow cab in NYC, a double-decker in London, goats in Dakar…).
 * No three.js, no DOM, no RNG.
 *
 * DETERMINISM CONTRACT (online-critical): a mover's pose is a pure function of
 * the SNAPSHOTTED race clock (`SimCore.raceTime`) — never of local step counts
 * or wall time — so the server sim and every predicting client always agree on
 * where the bus is without a single extra byte in the snapshot.
 *
 * Motion: a triangle-wave shuttle between the two road edges (±span) with a
 * DWELL pause parked at each side — the crossing is the hazard, the dwell is
 * the safe timing window you learn. Phase 0 starts the cycle mid-dwell on the
 * spawn side, so the grid + countdown never begin with traffic mid-road.
 */

/** A mover resolved onto the track (frame captured once, like PlacedFeature). */
export interface PlacedMover {
  style: string;
  /** Anchor on the road centreline (the crossing point). */
  x: number;
  z: number;
  /** Track right vector at the anchor (unit) — the crossing axis. */
  rightX: number;
  rightZ: number;
  /** Track forward at the anchor (unit) — kept for view framing. */
  dirX: number;
  dirZ: number;
  /** Crossing half-extent: dwells at -span, crosses to +span (and back). */
  span: number;
  /** Crossing speed (u/s) while moving. */
  speed: number;
  /** Seconds parked at each edge between crossings. */
  dwell: number;
  /** Collision capsule: half-length along the mover's FACING axis + radius. */
  halfLen: number;
  radius: number;
  /** Phase offset in SECONDS into the cycle (staggers multiple movers). */
  phase: number;
}

/** One evaluated pose: centre position on the crossing axis + motion sense. */
export interface MoverPose {
  /** Signed lateral offset from the anchor along `right` (world units). */
  lat: number;
  /** -1 crossing toward -span, +1 toward +span, 0 parked (dwelling). */
  moving: -1 | 0 | 1;
  /** Which way the BODY faces: +1 = +right, -1 = -right (held while parked). */
  facing: 1 | -1;
  /** 0..1 progress through the current cycle (for view gait animation). */
  cycleT: number;
}

/** Resolve a city's mover specs onto a generated track (once per race). */
export function placeMovers(track: Track, specs: readonly MoverSpec[]): PlacedMover[] {
  return specs.map((m) => {
    const s = track.sampleAt(m.at * track.finishDistance);
    return {
      style: m.style,
      x: s.x,
      z: s.z,
      rightX: s.rightX,
      rightZ: s.rightZ,
      dirX: Math.sin(s.dir), // §6.1: heading → forward (sin h, cos h)
      dirZ: Math.cos(s.dir),
      span: m.span ?? 13,
      speed: m.speed ?? 6,
      dwell: m.dwell ?? 3,
      halfLen: m.halfLen ?? 1.7,
      radius: m.radius ?? 1.3,
      phase: m.phase ?? 0,
    };
  });
}

/** Full shuttle cycle length in seconds (dwell + cross + dwell + cross back). */
export function moverPeriod(m: Pick<PlacedMover, 'span' | 'speed' | 'dwell'>): number {
  return 2 * (m.dwell + (2 * m.span) / m.speed);
}

/**
 * Where a mover is at race-clock `time` (seconds since GO). Piecewise cycle:
 *   [0, dwell)                    parked at -span (facing +, ready to cross)
 *   [dwell, dwell+cross)          crossing -span → +span
 *   [dwell+cross, 2·dwell+cross)  parked at +span (facing -, ready to return)
 *   [2·dwell+cross, period)       crossing +span → -span
 * Negative time (pre-GO / countdown) clamps to the cycle start: parked.
 */
export function moverPoseAt(
  m: Pick<PlacedMover, 'span' | 'speed' | 'dwell' | 'phase'>,
  time: number,
): MoverPose {
  const cross = (2 * m.span) / m.speed;
  const period = 2 * (m.dwell + cross);
  let t = (time + m.phase) % period;
  if (t < 0) t += period; // JS % keeps sign; a negative clock still lands in-cycle
  if (time <= 0) t = Math.min(t, Math.max(0, m.dwell - 0.001)); // parked through the countdown
  const cycleT = t / period;
  if (t < m.dwell) return { lat: -m.span, moving: 0, facing: 1, cycleT };
  t -= m.dwell;
  if (t < cross) return { lat: -m.span + m.speed * t, moving: 1, facing: 1, cycleT };
  t -= cross;
  if (t < m.dwell) return { lat: m.span, moving: 0, facing: -1, cycleT };
  t -= m.dwell;
  return { lat: m.span - m.speed * t, moving: -1, facing: -1, cycleT };
}

/** World-space centre of a mover at `time` (anchor + right · lat). */
export function moverCenterAt(m: PlacedMover, time: number): { x: number; z: number } {
  const p = moverPoseAt(m, time);
  return { x: m.x + m.rightX * p.lat, z: m.z + m.rightZ * p.lat };
}

/**
 * Body (car circle / ball sphere) vs a mover CAPSULE (segment along the
 * mover's facing axis, i.e. the crossing direction — a bus is long, not
 * round). Same DIVERTER response as static obstacles (M51 doctrine: slide
 * around tangentially, keep most speed, never bounce backward) PLUS the
 * mover's own velocity weighs in: a bus rolling into a parked car shoves it
 * along the crossing axis instead of trapping it inside the capsule.
 */
export function resolveMover(
  px: number,
  pz: number,
  bodyRadius: number,
  m: PlacedMover,
  time: number,
  vx: number,
  vz: number,
  keep: number,
): WallResolution | null {
  const pose = moverPoseAt(m, time);
  const cx = m.x + m.rightX * pose.lat;
  const cz = m.z + m.rightZ * pose.lat;
  // Closest point on the mover's spine segment (along ±right) to the body.
  const relX = px - cx;
  const relZ = pz - cz;
  let along = relX * m.rightX + relZ * m.rightZ;
  along = Math.max(-m.halfLen, Math.min(m.halfLen, along));
  const sx = cx + m.rightX * along;
  const sz = cz + m.rightZ * along;
  const dx = px - sx;
  const dz = pz - sz;
  const d = Math.hypot(dx, dz);
  const minD = bodyRadius + m.radius;
  if (d >= minD) return null;
  // Dead-centre overlap (the mover rolled exactly onto the body): push along
  // the mover's own travel so it ploughs the body ahead of its nose.
  let nx: number;
  let nz: number;
  if (d < 1e-6) {
    nx = m.rightX * (pose.moving >= 0 ? 1 : -1);
    nz = m.rightZ * (pose.moving >= 0 ? 1 : -1);
  } else {
    nx = dx / d;
    nz = dz / d;
  }
  const over = minD - Math.max(d, 0);
  // The mover's velocity along the crossing axis (0 while dwelling).
  const mvx = m.rightX * m.speed * pose.moving;
  const mvz = m.rightZ * m.speed * pose.moving;
  // Relative approach: is the body moving INTO the mover (or the mover into it)?
  const into = -((vx - mvx) * nx + (vz - mvz) * nz);
  let nvx = vx;
  let nvz = vz;
  if (into > 0) {
    // Diverter: follow the existing tangential motion; a dead-centre hit picks
    // a side deterministically (same rule as resolveObstacle).
    const tx = -nz;
    const tz = nx;
    let s = vx * tx + vz * tz;
    if (Math.abs(s) < 1e-3) s = vx * nz - vz * nx >= 0 ? 1 : -1;
    const sign = Math.sign(s) || 1;
    const speed = Math.hypot(vx, vz);
    let ddx = tx * sign * 0.94 + nx * 0.34;
    let ddz = tz * sign * 0.94 + nz * 0.34;
    const mm = Math.hypot(ddx, ddz) || 1;
    ddx /= mm;
    ddz /= mm;
    nvx = ddx * speed * keep;
    nvz = ddz * speed * keep;
    // Inherit the shove: never end up moving INTO the mover slower than IT
    // moves — a rolling bus carries you along rather than grinding over you.
    const outSpeed = nvx * nx + nvz * nz;
    const moverOut = mvx * nx + mvz * nz;
    if (moverOut > outSpeed) {
      nvx += nx * (moverOut - outSpeed);
      nvz += nz * (moverOut - outSpeed);
    }
  }
  return { pushX: nx * over, pushZ: nz * over, vx: nvx, vz: nvz };
}
